import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Shared parts of {@link DsCombobox} and {@link DsMultiSelect}: the list that
 * opens under a field. It is portalled into the `[data-ds="eos"]` scope and
 * positioned fixed, so a modal body or a scrolling toolbar never clips it, and
 * it opens upwards when there is no room below.
 */

const GAP = 2;
const MAX_HEIGHT = 280;
const VIEWPORT_MARGIN = 8;

interface FloatingPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

function useFloatingPosition(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  minWidth: number,
): FloatingPosition | null {
  const [position, setPosition] = useState<FloatingPosition | null>(null);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.max(rect.width, minWidth);
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    const below = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
    const above = rect.top - GAP - VIEWPORT_MARGIN;
    if (below >= Math.min(MAX_HEIGHT, 160) || below >= above) {
      setPosition({
        left,
        width,
        top: rect.bottom + GAP,
        maxHeight: Math.min(MAX_HEIGHT, below),
      });
    } else {
      setPosition({
        left,
        width,
        bottom: window.innerHeight - rect.top + GAP,
        maxHeight: Math.min(MAX_HEIGHT, above),
      });
    }
  }, [anchorRef, minWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    update();
    window.addEventListener('resize', update);
    // Capture: also follows a scrolling modal body or table around the field.
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  return position;
}

interface FloatingListProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  id?: string;
  role?: string;
  ariaLabel?: string;
  ariaMultiselectable?: boolean;
  /** The list is at least this wide, even under a narrow field. */
  minWidth?: number;
  className?: string;
  children: ReactNode;
}

/** The popover of a field; renders nothing while closed. */
export const FloatingList = forwardRef<HTMLDivElement, FloatingListProps>(
  function FloatingList(
    {
      anchorRef,
      open,
      id,
      role,
      ariaLabel,
      ariaMultiselectable,
      minWidth = 0,
      className = '',
      children,
    },
    ref,
  ) {
    const position = useFloatingPosition(anchorRef, open, minWidth);
    if (!open || !position) return null;
    const style: CSSProperties = {
      left: position.left,
      width: position.width,
      top: position.top,
      bottom: position.bottom,
      maxHeight: position.maxHeight,
    };
    const target = document.querySelector('[data-ds="eos"]') ?? document.body;
    return createPortal(
      <div
        ref={ref}
        id={id}
        role={role}
        aria-label={ariaLabel}
        aria-multiselectable={ariaMultiselectable}
        className={['ds-floating-list', className].filter(Boolean).join(' ')}
        style={style}
        // Keep the focus where it is while an entry is clicked; a search field
        // inside the list can still be clicked into.
        onMouseDown={(e) => {
          if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
        }}
      >
        {children}
      </div>,
      target,
    );
  },
);
